import * as React from 'react';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import { Button } from 'reactstrap';

import { ClipboardService } from '@app/Services/ClipboardService';

import { Icon } from '@app/Components/Icon';

import './_error-view.scss';

type ErrorViewProps = {
    error: Error | null;
    componentStack: string | null;
    onClose?: () => void;
    isInDialog?: boolean;
};

const COPIED_FEEDBACK_MS = 2000;

@observer
export class ErrorView extends React.Component<ErrorViewProps> {
    @observable private _isCopied = false;
    private _copiedTimer?: number;

    componentWillUnmount() {
        window.clearTimeout(this._copiedTimer);
    }

    render() {
        const { error, onClose, isInDialog } = this.props;
        const technicalDetails = this._technicalDetails;
        const isOutdatedBuild = error?.name === 'ChunkLoadError';

        return (
            <div className={'error-view' + (isInDialog ? ' in-dialog' : '')}>
                <div className="error-view-header">
                    <div className="error-view-icon">
                        <Icon name={isOutdatedBuild ? 'sync-alt' : 'exclamation-triangle'} />
                    </div>
                    <div className="error-view-heading">
                        <div className="error-view-title">Something went wrong</div>
                        <div className="error-view-subtitle">{this._getHint(isOutdatedBuild)}</div>
                    </div>
                </div>

                <div className="error-view-message">{error?.message ?? 'Error'}</div>

                <div className="error-view-actions">
                    {onClose && (
                        <Button color="primary" size="sm" onClick={onClose}>
                            Close
                        </Button>
                    )}
                    {(isOutdatedBuild || (!onClose && !isInDialog)) && (
                        <Button color={onClose ? 'secondary' : 'primary'} outline={!!onClose} size="sm" onClick={this._reload}>
                            <Icon name="redo" className="mr-1" /> Reload page
                        </Button>
                    )}
                    <Button color="secondary" outline size="sm" onClick={this._copyDetails}>
                        <Icon name={this._isCopied ? 'check' : 'copy'} className="mr-1" /> {this._isCopied ? 'Copied' : 'Copy details'}
                    </Button>
                </div>

                {technicalDetails && (
                    <details className="error-view-details">
                        <summary>Technical details</summary>
                        <pre className="error-view-stack">{technicalDetails}</pre>
                    </details>
                )}
            </div>
        );
    }

    private _getHint(isOutdatedBuild: boolean) {
        if (isOutdatedBuild) return 'A newer version of the application is available. Reload the page to continue.';
        if (this.props.onClose) return 'Close it to continue working. If the problem persists, please contact support.';
        if (this.props.isInDialog) return 'Close the dialog and try again. If the problem persists, please contact support.';
        return 'Try reloading the page. If the problem persists, please contact support.';
    }

    private get _technicalDetails() {
        const { error, componentStack } = this.props;
        const errorStack = error?.stack ?? [error?.name, error?.message].filter(Boolean).join(': ');

        return [errorStack, componentStack].map(x => x?.trim()).filter(Boolean).join('\n\nComponent stack:\n');
    }

    private _reload() {
        window.location.reload();
    }

    @action.bound
    private _copyDetails() {
        if (!ClipboardService.copyToClipboard(this._technicalDetails)) return;

        this._setCopied(true);
        window.clearTimeout(this._copiedTimer);
        this._copiedTimer = window.setTimeout(() => this._setCopied(false), COPIED_FEEDBACK_MS);
    }

    @action.bound
    private _setCopied(isCopied: boolean) {
        this._isCopied = isCopied;
    }
}
